(() => {
  const storageKey = 'indieBookhavenRecentlyViewedV2';
  const productSelector = '[data-recently-viewed-product]';
  const carouselSelector = '[data-recently-viewed-carousel]';
  const trackSelector = '[data-recently-viewed-track]';
  const cardTemplateSelector = '[data-recently-viewed-card-template]';
  const actionTemplateSelector = '[data-recently-viewed-action-template]';
  const resultItemSelector = '[data-featured-products-carousel-result-item]';
  const cardFooterSelector = '.featured-products-carousel__footer';
  const deferredAttribute = 'data-carousel-deferred';
  const desktopMediaQuery = window.matchMedia('(min-width: 990px)');

  const getStoredItems = () => {
    try {
      return JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    } catch (error) {
      return [];
    }
  };

  const setStoredItems = (items) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(items.slice(0, 8)));
    } catch (error) {
      // Recently viewed is progressive enhancement. The page should still work without storage.
    }
  };

  const normalizeUrl = (url) => {
    if (!url) return '';

    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return url.split('?')[0].split('#')[0];
    }
  };

  const getItemKey = (item) => {
    if (item.id) return `id:${item.id}`;
    if (item.handle) return `handle:${item.handle}`;
    return `url:${normalizeUrl(item.url)}`;
  };

  const dedupeItems = (items) => {
    const seen = new Set();

    return items.filter((item) => {
      const key = getItemKey(item);
      if (!key || seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  };

  const createCard = (item) => {
    if (typeof item.cardHtml !== 'string' || !item.cardHtml.trim()) return null;

    const template = document.createElement('template');
    template.innerHTML = item.cardHtml;
    const card = template.content.querySelector(resultItemSelector);
    const titleLink = card?.querySelector('.featured-products-carousel__title a');

    // Saved cards can predate the server-rendered title/name alignment.
    if (titleLink) {
      const fullTitle = (titleLink.getAttribute('title') || item.title || titleLink.textContent).trim();
      const characters = Array.from(fullTitle);
      let displayTitle = fullTitle;
      if (characters.length > 60) {
        let prefix = characters.slice(0, 59).join('');
        if (characters[59] !== ' ' && !prefix.endsWith(' ') && prefix.includes(' ')) {
          prefix = prefix.slice(0, prefix.lastIndexOf(' '));
        }
        displayTitle = `${prefix.trimEnd()}…`;
      }
      titleLink.textContent = displayTitle;
      titleLink.title = fullTitle;
      titleLink.removeAttribute('aria-label');
    }
    return card;
  };

  const storeProduct = (product) => {
    const id = product.dataset.productId || '';
    const handle = product.dataset.productHandle || '';
    const title = product.dataset.productTitle;
    const url = product.dataset.productUrl;
    const cardHtml = product.querySelector(cardTemplateSelector)?.innerHTML.trim() || '';
    const actionHtml = Object.fromEntries(
      Array.from(product.querySelectorAll(actionTemplateSelector)).map((template) => [
        template.dataset.recentlyViewedActionTemplate,
        template.innerHTML.trim(),
      ])
    );

    if (!title || !url || !cardHtml) return;

    const nextItem = { id, handle, title, url: normalizeUrl(url), cardHtml, actionHtml };
    const nextKey = getItemKey(nextItem);
    const currentItems = dedupeItems(getStoredItems()).filter((item) => getItemKey(item) !== nextKey);
    currentItems.unshift(nextItem);
    setStoredItems(currentItems);
    window.dispatchEvent(new CustomEvent('indieBookhaven:recently-viewed-updated'));
  };

  const renderCarousel = (carousel) => {
    const track = carousel.querySelector(trackSelector);
    if (!track) return;

    const limit = Number.parseInt(carousel.dataset.limit, 10) || 5;
    const requiredItems = Number.parseInt(carousel.dataset.requiredItems, 10) || 1;
    const showUnavailable = carousel.dataset.showUnavailable !== 'false';
    const showAddToCart = carousel.dataset.showAddToCart === 'true';
    const enableBookshopFallback = carousel.dataset.enableBookshopFallback === 'true';
    const currentProduct = document.querySelector(productSelector);
    const currentItem = currentProduct
      ? {
          id: currentProduct.dataset.productId || '',
          handle: currentProduct.dataset.productHandle || '',
          url: currentProduct.dataset.productUrl || '',
        }
      : null;
    const currentKey = currentItem ? getItemKey(currentItem) : '';
    const validItems = [];
    const items = [];

    dedupeItems(getStoredItems()).forEach((item) => {
      const card = createCard(item);
      if (!card) return;

      validItems.push(item);

      if (getItemKey(item) === currentKey) return;
      if (!showUnavailable && card.dataset.available !== 'true') return;

      items.push({ item, card });
    });

    items.splice(limit);
    const shouldHide = items.length < requiredItems;

    setStoredItems(validItems);
    track.innerHTML = '';

    if (shouldHide) {
      carousel.hidden = true;
      carousel.dataset.carouselAvailability = 'empty';
      carousel.dispatchEvent(new CustomEvent('carousel:availability-changed', { bubbles: true }));
      return;
    }

    items.forEach(({ item, card }) => {
      if (showAddToCart) {
        const actionMode = enableBookshopFallback ? 'bookshop' : 'details';
        const actionMarkup = item.actionHtml?.[actionMode] || '';
        const footer = card.querySelector(cardFooterSelector);

        if (actionMarkup && footer) {
          const actionTemplate = document.createElement('template');
          actionTemplate.innerHTML = actionMarkup;
          footer.appendChild(actionTemplate.content.cloneNode(true));
        }
      }

      track.appendChild(card);
    });

    carousel.hidden = false;
    carousel.dataset.carouselAvailability = 'ready';
    carousel.dispatchEvent(new CustomEvent('carousel:availability-changed', { bubbles: true }));
    carousel.dispatchEvent(new CustomEvent('carousel:content-updated', { bubbles: true }));
  };

  const init = (scope = document) => {
    scope.querySelectorAll(productSelector).forEach((product) => {
      if (product.dataset.recentlyViewedTracked === 'true') return;

      product.dataset.recentlyViewedTracked = 'true';
      storeProduct(product);
    });

    scope.querySelectorAll(carouselSelector).forEach((carousel) => {
      if (carousel.hasAttribute(deferredAttribute)) return;
      renderCarousel(carousel);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('indieBookhaven:recently-viewed-updated', () => init());

  document.addEventListener('carousel:fallback-activate', (event) => {
    event.target.querySelectorAll(carouselSelector).forEach((carousel) => {
      carousel.removeAttribute(deferredAttribute);
      renderCarousel(carousel);
    });
  });

  if (desktopMediaQuery.addEventListener) {
    desktopMediaQuery.addEventListener('change', () => init());
  }
})();
